import React, { useMemo, useState, useCallback } from "react";
import {
  FiX,
  FiFilter,
  FiRotateCcw,
  FiChevronDown,
  FiMapPin,
  FiGrid,
  FiDroplet,
  FiDollarSign,
  FiCheck,
  FiPackage,
  FiShield,
} from "react-icons/fi";
import Dropdown from "../../../components/common/Dropdown";
import CategoryHierarchyDropdown from "pages/Category/components/CategoryHierarchyDropdown";

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

/** Collapsible section with active filter count badge on header */
const FilterSection = ({
  icon: Icon,
  title,
  activeCount = 0,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden transition-shadow hover:shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50/70 hover:bg-gray-100/80 transition-colors"
      >
        <span className="flex items-center gap-2.5 text-sm font-semibold text-gray-800">
          <Icon size={16} className="text-gray-500 flex-shrink-0" />
          {title}
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold bg-primary text-white rounded-full leading-none">
              {activeCount}
            </span>
          )}
        </span>
        <FiChevronDown
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          open ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-4 space-y-4 border-t border-gray-100">
          {children}
        </div>
      </div>
    </div>
  );
};

/** Chip selector — picks one value from a list. "All" chip deselects. */
const ChipSelect = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isActive = value === opt.value;
      const isAll = opt.value === "";

      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(isActive && !isAll ? "" : opt.value)}
          className={`
            relative inline-flex items-center gap-1.5 px-3 py-2 rounded-lg
            text-xs sm:text-[13px] font-medium border transition-all duration-150
            ${
              isActive && !isAll
                ? "bg-primary/10 border-primary/40 text-primary ring-1 ring-primary/15"
                : isAll && value === ""
                  ? "bg-gray-100 border-gray-200 text-gray-500"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
            }
          `}
        >
          {opt.colorDot && (
            <span
              className="w-3.5 h-3.5 rounded-full border border-gray-200/80 flex-shrink-0"
              style={{ background: opt.colorDot }}
            />
          )}
          {opt.label}
          {isActive && !isAll && (
            <FiCheck size={12} className="text-primary flex-shrink-0" />
          )}
        </button>
      );
    })}
  </div>
);

/** Toggle chip — boolean on/off with optional variant colouring */
const ToggleChip = ({
  label,
  icon: Icon,
  active,
  onToggle,
  variant = "default",
}) => {
  const styles = {
    default: active
      ? "bg-primary/10 border-primary/40 text-primary ring-1 ring-primary/15"
      : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50",
    success: active
      ? "bg-emerald-50 border-emerald-400 text-emerald-700 ring-1 ring-emerald-200"
      : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50",
    warning: active
      ? "bg-amber-50 border-amber-400 text-amber-700 ring-1 ring-amber-200"
      : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50",
  };

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 px-3 py-2 sm:px-3.5 sm:py-2.5 rounded-xl
        text-xs sm:text-[13px] font-medium border transition-all duration-150 ${styles[variant]}`}
    >
      {Icon && <Icon size={14} className="flex-shrink-0" />}
      {label}
      {active && <FiCheck size={12} className="flex-shrink-0" />}
    </button>
  );
};

/** Min/Max range input pair with optional ₹ prefix */
const RangeInput = ({
  label,
  hint,
  minVal,
  maxVal,
  onMinChange,
  onMaxChange,
  minPlaceholder = "Min",
  maxPlaceholder = "Max",
  step = "1",
  prefix,
}) => (
  <div>
    <div className="flex items-baseline justify-between mb-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {hint && (
        <span className="text-[11px] text-gray-400 font-normal">{hint}</span>
      )}
    </div>
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={minVal}
          onChange={(e) => onMinChange(e.target.value)}
          min="0"
          step={step}
          className={`w-full ${
            prefix ? "pl-7" : "pl-3"
          } pr-3 py-2.5 border border-gray-200 rounded-lg text-sm
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
            transition-all placeholder:text-gray-300`}
          placeholder={minPlaceholder}
        />
      </div>
      <div className="w-4 h-px bg-gray-300 flex-shrink-0" />
      <div className="relative flex-1">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={maxVal}
          onChange={(e) => onMaxChange(e.target.value)}
          min="0"
          step={step}
          className={`w-full ${
            prefix ? "pl-7" : "pl-3"
          } pr-3 py-2.5 border border-gray-200 rounded-lg text-sm
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
            transition-all placeholder:text-gray-300`}
          placeholder={maxPlaceholder}
        />
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────────────────

const MoreFiltersPanel = ({
  filters,
  onFilterChange,
  onClose,
  availableBranches = [],
  branches = [],
  categories = [],
  categoriesLoading = false,
}) => {
  // ── handlers ──
  const handleChange = useCallback(
    (field, value) => {
      onFilterChange((prev) => ({ ...prev, [field]: value, page: 1 }));
    },
    [onFilterChange],
  );

  // ── branch helpers ──
  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        _id: b.code,
        name: `${b.code} – ${b.name || "Unknown"}`,
      })),
    [branches],
  );

  const selectedBranches = useMemo(() => {
    if (!filters.branchCode) return null;
    const codes = Array.isArray(filters.branchCode)
      ? filters.branchCode
      : [filters.branchCode];
    return branchOptions.filter((o) => codes.includes(o._id));
  }, [filters.branchCode, branchOptions]);

  const handleBranchChange = useCallback(
    (selected) => {
      if (!selected || selected.length === 0) {
        handleChange("branchCode", "");
      } else {
        const codes = selected.map((i) => i._id);
        handleChange("branchCode", codes.length === 1 ? codes[0] : codes);
      }
    },
    [handleChange],
  );

  // ── section-level active filter counts ──
  const quickCount = useMemo(() => {
    let c = 0;
    if (filters.isFeatured === "true") c++;
    if (filters.isNewArrival === "true") c++;
    if (filters.isBestseller === "true") c++;
    if (filters.inStock) c++;
    if (filters.isHallmarked) c++;
    return c;
  }, [filters]);

  const scopeCount = useMemo(() => {
    let c = 0;
    if (filters.branchCode) c++;
    if (filters.category) c++;
    return c;
  }, [filters.branchCode, filters.category]);

  const productCount = useMemo(() => {
    let c = 0;
    if (filters.subType) c++;
    if (filters.gender) c++;
    return c;
  }, [filters]);

  const metalCount = useMemo(() => {
    let c = 0;
    if (filters.purity) c++;
    if (filters.metalColor) c++;
    return c;
  }, [filters]);

  const priceWeightCount = useMemo(() => {
    let c = 0;
    if (filters.minPrice || filters.maxPrice) c++;
    if (filters.minGrossWeight || filters.maxGrossWeight) c++;
    return c;
  }, [filters]);

  const totalActive =
    quickCount + scopeCount + productCount + metalCount + priceWeightCount;

  // ── clear all ──
  const clearAllFilters = useCallback(() => {
    onFilterChange((prev) => ({
      search: prev.search,
      type: prev.type,
      status: prev.status,
      branchCode: "",
      inStock: "",
      isHallmarked: "",
      minGrossWeight: "",
      maxGrossWeight: "",
      purity: "",
      subType: "",
      gender: "",
      isFeatured: "",
      isNewArrival: "",
      isBestseller: "",
      metalColor: "",
      minPrice: "",
      maxPrice: "",
      category: "",
      page: 1,
      limit: prev.limit || 20,
    }));
  }, [onFilterChange]);

  // ── option lists ──
  const subTypeOptions = [
    { value: "", label: "All" },
    { value: "RING", label: "Ring" },
    { value: "NECKLACE", label: "Necklace" },
    { value: "EARRING", label: "Earring" },
    { value: "BRACELET", label: "Bracelet" },
    { value: "PENDANT", label: "Pendant" },
    { value: "CHAIN", label: "Chain" },
    { value: "BANGLE", label: "Bangle" },
    { value: "ANKLET", label: "Anklet" },
    { value: "NOSE_PIN", label: "Nose Pin" },
    { value: "MANGALSUTRA", label: "Mangalsutra" },
  ];

  const genderOptions = [
    { value: "", label: "All" },
    { value: "FEMALE", label: "Women" },
    { value: "MALE", label: "Men" },
    { value: "UNISEX", label: "Unisex" },
    { value: "KIDS", label: "Kids" },
  ];

  const purityOptions = [
    { value: "", label: "All" },
    { value: "24K", label: "24K" },
    { value: "22K", label: "22K" },
    { value: "18K", label: "18K" },
    { value: "14K", label: "14K" },
    { value: "925", label: "925 Silver" },
    { value: "950", label: "950 Plat." },
  ];

  const metalColorOptions = [
    { value: "", label: "All" },
    { value: "YELLOW", label: "Yellow", colorDot: "#FFD700" },
    { value: "WHITE", label: "White", colorDot: "#E8E8E8" },
    { value: "ROSE", label: "Rose", colorDot: "#E8A090" },
    {
      value: "TWO_TONE",
      label: "Two Tone",
      colorDot: "linear-gradient(135deg, #FFD700 50%, #E8E8E8 50%)",
    },
    {
      value: "THREE_TONE",
      label: "Three Tone",
      colorDot:
        "linear-gradient(135deg, #FFD700 33%, #E8E8E8 33%, #E8E8E8 66%, #E8A090 66%)",
    },
  ];

  const priceHint = useMemo(() => {
    if (!filters.branchCode) return "primary branch";
    if (Array.isArray(filters.branchCode))
      return `avg. ${filters.branchCode.length} branches`;
    return filters.branchCode;
  }, [filters.branchCode]);

  // ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* ─── Sticky header ─── */}
      <div className="flex-shrink-0 z-20 bg-white border-b border-gray-100">
        <div className="px-4 lg:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
              <FiFilter size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900 leading-tight">
                Filters
              </h3>
              {totalActive > 0 && (
                <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                  {totalActive} active
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {totalActive > 0 && (
              <button
                onClick={clearAllFilters}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500
                  hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <FiRotateCcw size={12} />
                Clear all
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 text-gray-400
                hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close filters"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Scope Filters (Branch + Category) ───
           Pinned above the scroll area with overflow-visible so
           dropdown menus are NOT clipped                          */}
      <div className="flex-shrink-0 relative z-30 bg-white border-b border-gray-100 overflow-visible">
        <div className="px-4 lg:px-6 py-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Scope
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Branch */}
            <div className="relative">
              <Dropdown
                label="Branch Location"
                options={branchOptions}
                selected={selectedBranches}
                onChange={handleBranchChange}
                multiple={true}
                placeholder="All Branches"
                clearable={true}
                helperText="Filter by branch availability"
              />
            </div>

            {/* Category */}
            <div className="relative">
              <CategoryHierarchyDropdown
                label="Category"
                categories={categories}
                value={filters.category || null}
                onChange={(id) => handleChange("category", id || "")}
                placeholder="All Categories"
                maxPathLength={35}
                allowClear={true}
                disabled={categoriesLoading}
              />
              {categoriesLoading && (
                <p className="text-[11px] text-gray-400 mt-1">Loading…</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Scrollable body (no dropdowns here — safe to clip) ─── */}
      <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 px-4 lg:px-6 py-4 space-y-3">
        {/* Quick toggles — always visible, no accordion */}
        <div className="pb-1">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
            Quick Filters
          </p>
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              label="In Stock"
              icon={FiPackage}
              active={filters.inStock === "true"}
              onToggle={() =>
                handleChange(
                  "inStock",
                  filters.inStock === "true" ? "" : "true",
                )
              }
              variant="success"
            />
            <ToggleChip
              label="Hallmarked"
              icon={FiShield}
              active={filters.isHallmarked === "true"}
              onToggle={() =>
                handleChange(
                  "isHallmarked",
                  filters.isHallmarked === "true" ? "" : "true",
                )
              }
              variant="warning"
            />
            <ToggleChip
              label="Out of Stock"
              active={filters.inStock === "false"}
              onToggle={() =>
                handleChange(
                  "inStock",
                  filters.inStock === "false" ? "" : "false",
                )
              }
            />
            <ToggleChip
              label="Featured"
              active={filters.isFeatured === "true"}
              onToggle={() =>
                handleChange(
                  "isFeatured",
                  filters.isFeatured === "true" ? "" : "true",
                )
              }
            />
            <ToggleChip
              label="New Arrivals"
              active={filters.isNewArrival === "true"}
              onToggle={() =>
                handleChange(
                  "isNewArrival",
                  filters.isNewArrival === "true" ? "" : "true",
                )
              }
            />
            <ToggleChip
              label="Bestsellers"
              active={filters.isBestseller === "true"}
              onToggle={() =>
                handleChange(
                  "isBestseller",
                  filters.isBestseller === "true" ? "" : "true",
                )
              }
            />
          </div>
        </div>

        {/* Product Details — chips only, no dropdowns */}
        <FilterSection
          icon={FiGrid}
          title="Product Details"
          activeCount={productCount}
          defaultOpen={true}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jewelry Type
            </label>
            <ChipSelect
              options={subTypeOptions}
              value={filters.subType || ""}
              onChange={(v) => handleChange("subType", v)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender
            </label>
            <ChipSelect
              options={genderOptions}
              value={filters.gender || ""}
              onChange={(v) => handleChange("gender", v)}
            />
          </div>
        </FilterSection>

        {/* Metal & Purity */}
        <FilterSection
          icon={FiDroplet}
          title="Metal & Purity"
          activeCount={metalCount}
          defaultOpen={metalCount > 0}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purity
            </label>
            <ChipSelect
              options={purityOptions}
              value={filters.purity || ""}
              onChange={(v) => handleChange("purity", v)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Metal Color
            </label>
            <ChipSelect
              options={metalColorOptions}
              value={filters.metalColor || ""}
              onChange={(v) => handleChange("metalColor", v)}
            />
          </div>
        </FilterSection>

        {/* Price & Weight */}
        <FilterSection
          icon={FiDollarSign}
          title="Price & Weight"
          activeCount={priceWeightCount}
          defaultOpen={priceWeightCount > 0}
        >
          <RangeInput
            label="Price Range"
            hint={priceHint}
            prefix="₹"
            minVal={filters.minPrice || ""}
            maxVal={filters.maxPrice || ""}
            onMinChange={(v) => handleChange("minPrice", v)}
            onMaxChange={(v) => handleChange("maxPrice", v)}
            step="100"
            minPlaceholder="0"
            maxPlaceholder="No limit"
          />

          <RangeInput
            label="Gross Weight"
            hint="grams"
            minVal={filters.minGrossWeight}
            maxVal={filters.maxGrossWeight}
            onMinChange={(v) => handleChange("minGrossWeight", v)}
            onMaxChange={(v) => handleChange("maxGrossWeight", v)}
            step="0.1"
            minPlaceholder="0g"
            maxPlaceholder="No limit"
          />
        </FilterSection>
      </div>

      {/* ─── Sticky footer ─── */}
      <div className="flex-shrink-0 bg-white border-t border-gray-100">
        <div className="px-4 lg:px-6 py-3.5 flex items-center gap-3">
          {/* Mobile clear button */}
          <button
            onClick={clearAllFilters}
            disabled={totalActive === 0}
            className="sm:hidden flex items-center justify-center gap-1.5 px-4 py-2.5
              text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200
              rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiRotateCcw size={14} />
            Clear
          </button>

          {/* Apply / Show Results */}
          <button
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5
              text-sm font-semibold text-white bg-primary hover:bg-primary/90
              active:scale-[0.98] rounded-xl transition-all shadow-sm"
          >
            Show Results
            {totalActive > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold bg-white/25 rounded-full leading-none">
                {totalActive}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoreFiltersPanel;
